import React, { useEffect, useState } from 'react';
import { Search, Activity } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import toast from 'react-hot-toast';

const ActivityLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getActivityLogs();
      if (res.success && res.data) setLogs(res.data);
    } catch {
      toast.error('Failed to fetch logs');
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = logs.filter(
    (l) =>
      l.action?.toLowerCase().includes(search.toLowerCase()) ||
      l.email?.toLowerCase().includes(search.toLowerCase()) ||
      l.entity_type?.toLowerCase().includes(search.toLowerCase())
  );

  const actionColors: Record<string, string> = {
    update_user_role: 'text-blue-600 bg-blue-50',
    delete_user: 'text-red-500 bg-red-50',
    update_stock: 'text-amber-600 bg-amber-50',
    create: 'text-green-600 bg-green-50',
    update: 'text-purple-600 bg-purple-50',
    delete: 'text-red-500 bg-red-50',
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-widest uppercase text-nude mb-1">
          Super Admin
        </p>
        <h1 className="font-serif text-3xl text-primary">Activity Logs</h1>
      </div>

      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-nude"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="SEARCH LOGS"
          className="w-full bg-white border border-light pl-9 pr-4 py-3 text-xs tracking-widest outline-none focus:border-primary transition-colors"
        />
      </div>

      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Action', 'User', 'Entity', 'Details', 'Time'].map((h) => (
                <th
                  key={h}
                  className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase"
                >
                  No logs found
                </td>
              </tr>
            ) : (
              filtered.map((log) => (
                <tr
                  key={log.id}
                  className="border-b border-light hover:bg-light/50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <span
                      className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                        actionColors[log.action] || 'text-primary bg-light'
                      }`}
                    >
                      {log.action?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs font-medium">
                      {log.first_name} {log.last_name}
                    </p>
                    <p className="text-xs text-nude">{log.email}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs tracking-widest uppercase text-nude">
                      {log.entity_type || '—'}
                    </p>
                    {log.entity_id && (
                      <p className="text-xs text-nude font-mono truncate max-w-24">
                        {log.entity_id}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {log.metadata && (
                      <pre className="text-xs text-nude bg-light px-2 py-1 rounded max-w-48 truncate">
                        {JSON.stringify(log.metadata)}
                      </pre>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {new Date(log.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ActivityLogsPage;